import React, { useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { parseUsdc } from '../../utils/format';
import { RANK_NAMES, NUM_RANKS, NUM_MILESTONES, NUM_LEVELS } from '../../utils/constants';

export const RankLevelManager = () => {
  const { adminW } = useContracts();
  const notify = useNotify();
  const [busy, setBusy] = useState(null);

  // Rank state
  const [rIdx, setRIdx] = useState(0);
  const [rSelf, setRSelf] = useState('');
  const [rDir, setRDir] = useState('');
  const [rTeam, setRTeam] = useState('');
  const [rLegRank, setRLegRank] = useState(0);
  const [rLegCount, setRLegCount] = useState(0);

  // Level state
  const [lIdx, setLIdx] = useState(0);
  const [lPct, setLPct] = useState('');
  const [lReq, setLReq] = useState('');

  // Milestone state
  const [mIdx, setMIdx] = useState(0);
  const [mMem, setMMem] = useState('');
  const [mVol, setMVol] = useState('');

  // Rank pool bps
  const [bps, setBps] = useState(['40', '60', '80', '100', '120']);

  // ROI timeframe
  const [roiTime, setRoiTime] = useState('');

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const tx = await fn();
      notify.info(`${label}: submitted`);
      await tx.wait();
      notify.success(`${label}: success`);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="card mb-24">
        <div className="section-title">Rank Requirements</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Rank</label>
            <select className="select" value={rIdx} onChange={(e) => setRIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_RANKS }).map((_, i) => (
                <option key={i} value={i}>{i} — {RANK_NAMES[i + 1]}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Self Investment (USDC)</label>
            <input className="input" type="number" value={rSelf} onChange={(e) => setRSelf(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Active Directs</label>
            <input className="input" type="number" value={rDir} onChange={(e) => setRDir(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Team Volume (USDC)</label>
            <input className="input" type="number" value={rTeam} onChange={(e) => setRTeam(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Leg Rank (0 = none)</label>
            <input className="input" type="number" value={rLegRank} onChange={(e) => setRLegRank(Number(e.target.value))} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Leg Count</label>
            <input className="input" type="number" value={rLegCount} onChange={(e) => setRLegCount(Number(e.target.value))} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'rank' || !rSelf || !rDir || !rTeam}
          onClick={() => run('rank', () => adminW.updateRankRequirement(
            rIdx, parseUsdc(rSelf), rDir, parseUsdc(rTeam), rLegRank, rLegCount
          ))}>
          {busy === 'rank' && <ButtonSpinner />}Update Rank
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Level Config</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Level</label>
            <select className="select" value={lIdx} onChange={(e) => setLIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_LEVELS }).map((_, i) => (
                <option key={i} value={i}>L{i + 1}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Percentage (bps)</label>
            <input className="input" type="number" value={lPct} onChange={(e) => setLPct(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Required Directs</label>
            <input className="input" type="number" value={lReq} onChange={(e) => setLReq(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'level' || !lPct || lReq === ''}
          onClick={() => run('level', () => adminW.updateLevelConfig(lIdx, lPct, lReq))}>
          {busy === 'level' && <ButtonSpinner />}Update Level
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Milestone Requirements</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Milestone #</label>
            <select className="select" value={mIdx} onChange={(e) => setMIdx(Number(e.target.value))}>
              {Array.from({ length: NUM_MILESTONES }).map((_, i) => (
                <option key={i} value={i}>M{i + 1}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Active Members</label>
            <input className="input" type="number" value={mMem} onChange={(e) => setMMem(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Team Volume (USDC)</label>
            <input className="input" type="number" value={mVol} onChange={(e) => setMVol(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary"
          disabled={busy === 'ms' || !mMem || !mVol}
          onClick={() => run('ms', () => adminW.updateMilestoneRequirement(mIdx, mMem, parseUsdc(mVol)))}>
          {busy === 'ms' && <ButtonSpinner />}Update Milestone
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Rank Pool BPS (must sum to rankPoolTotalFee)</div>
        <div className="admin-form-grid">
          {bps.map((v, i) => (
            <div className="input-group" key={i}>
              <label className="input-label">{RANK_NAMES[i + 1]}</label>
              <input className="input" type="number" value={v}
                onChange={(e) => setBps(bps.map((x, j) => j === i ? e.target.value : x))} />
            </div>
          ))}
        </div>
        <button className="btn btn-primary" disabled={busy === 'bps'}
          onClick={() => run('bps', () => adminW.updateRankPoolBps(bps.map((x) => Number(x))))}>
          {busy === 'bps' && <ButtonSpinner />}Update Pool BPS
        </button>
      </div>

      <div className="card">
        <div className="section-title">ROI Timeframe</div>
        <div className="input-group">
          <label className="input-label">Seconds per ROI period (default 86400 = 1 day)</label>
          <input className="input" type="number" value={roiTime} onChange={(e) => setRoiTime(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={busy === 'roi' || !roiTime}
          onClick={() => run('roi', () => adminW.updateROITimeframe(roiTime))}>
          {busy === 'roi' && <ButtonSpinner />}Update Timeframe
        </button>
      </div>
    </div>
  );
};
